import { z } from 'zod';
import {
    CloverRpaCapabilityName,
    CloverRpaCapabilityStatus,
    CloverRpaWorkflowId,
    CloverRpaWorkflowLoadingOutcome,
    type CloverRpaCapability,
    type CloverRpaCapabilityMetadata,
    type CloverRpaWorkflowDescriptor,
    type CloverRpaWorkflowLoadingNormalizedResult,
    type CloverRpaWorkflowLoadingRequestDetails,
    type CloverRpaWorkflowLoadingResponseDetails,
} from '../../../types/ipg/clover/clover-rpa-workflow-loading.types';

export const CloverRpaCapabilityNameSchema: z.ZodType<
    (typeof CloverRpaCapabilityName)[keyof typeof CloverRpaCapabilityName]
> = z.enum([
    CloverRpaCapabilityName.CARD_PRESENT_TERMINAL_SALE,
    CloverRpaCapabilityName.CARD_NOT_PRESENT_TOKEN_SALE,
    CloverRpaCapabilityName.STORED_CARD_PAYMENT,
    CloverRpaCapabilityName.TRANSACTION_STATUS_LOOKUP,
    CloverRpaCapabilityName.TERMINAL_ABORT,
    CloverRpaCapabilityName.TERMINAL_TOKENIZATION,
    CloverRpaCapabilityName.ACH_PAYMENT,
]);

export const CloverRpaWorkflowIdSchema: z.ZodType<
    (typeof CloverRpaWorkflowId)[keyof typeof CloverRpaWorkflowId]
> = z.enum([
    CloverRpaWorkflowId.TERMINAL_SALE,
    CloverRpaWorkflowId.TERMINAL_ABORT,
    CloverRpaWorkflowId.RECORD_CARD_TOKEN,
    CloverRpaWorkflowId.CNP_TOKEN_SALE,
    CloverRpaWorkflowId.STORED_CARD_PAYMENT,
    CloverRpaWorkflowId.TRANSACTION_STATUS,
    CloverRpaWorkflowId.ACH_PAYMENT,
]);

export const CloverEnabledRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CloverRpaCapabilityStatus.ENABLED>;
}> = z
    .object({
        status: z.literal(CloverRpaCapabilityStatus.ENABLED),
    })
    .strict();

export const CloverDisabledRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CloverRpaCapabilityStatus.DISABLED>;
    disabledReason: z.ZodString;
}> = z
    .object({
        status: z.literal(CloverRpaCapabilityStatus.DISABLED),
        disabledReason: z.string().min(1),
    })
    .strict();

export const CloverUnsupportedRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CloverRpaCapabilityStatus.UNSUPPORTED>;
    unsupportedReason: z.ZodString;
}> = z
    .object({
        status: z.literal(CloverRpaCapabilityStatus.UNSUPPORTED),
        unsupportedReason: z.string().min(1),
    })
    .strict();

export const CloverRpaCapabilitySchema: z.ZodType<CloverRpaCapability> =
    z.discriminatedUnion('status', [
        CloverEnabledRpaCapabilitySchema,
        CloverDisabledRpaCapabilitySchema,
        CloverUnsupportedRpaCapabilitySchema,
    ]);

export const CloverRpaCapabilityMetadataSchema: z.ZodObject<{
    card_present_terminal_sale: typeof CloverRpaCapabilitySchema;
    card_not_present_token_sale: typeof CloverRpaCapabilitySchema;
    stored_card_payment: typeof CloverRpaCapabilitySchema;
    transaction_status_lookup: typeof CloverRpaCapabilitySchema;
    terminal_abort: typeof CloverRpaCapabilitySchema;
    terminal_tokenization: typeof CloverRpaCapabilitySchema;
    ach_payment: typeof CloverRpaCapabilitySchema;
}> = z
    .object({
        [CloverRpaCapabilityName.CARD_PRESENT_TERMINAL_SALE]:
            CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.CARD_NOT_PRESENT_TOKEN_SALE]:
            CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.STORED_CARD_PAYMENT]:
            CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.TRANSACTION_STATUS_LOOKUP]:
            CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.TERMINAL_ABORT]: CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.TERMINAL_TOKENIZATION]:
            CloverRpaCapabilitySchema,
        [CloverRpaCapabilityName.ACH_PAYMENT]: CloverRpaCapabilitySchema,
    })
    .strict() satisfies z.ZodType<CloverRpaCapabilityMetadata>;

export const CloverRpaWorkflowLoadingRequestDetailsSchema: z.ZodObject<{
    packageName: z.ZodLiteral<'clover-ipg'>;
    requestedWorkflowIds: z.ZodArray<typeof CloverRpaWorkflowIdSchema>;
}> = z
    .object({
        packageName: z.literal('clover-ipg'),
        requestedWorkflowIds: z.array(CloverRpaWorkflowIdSchema).min(1),
    })
    .strict() satisfies z.ZodType<CloverRpaWorkflowLoadingRequestDetails>;

export const CloverRpaWorkflowDescriptorSchema: z.ZodObject<{
    id: typeof CloverRpaWorkflowIdSchema;
    capabilityName: typeof CloverRpaCapabilityNameSchema;
    enabled: z.ZodBoolean;
    showInMenu: z.ZodBoolean;
}> = z
    .object({
        id: CloverRpaWorkflowIdSchema,
        capabilityName: CloverRpaCapabilityNameSchema,
        enabled: z.boolean(),
        showInMenu: z.boolean(),
    })
    .strict() satisfies z.ZodType<CloverRpaWorkflowDescriptor>;

export const CloverRpaWorkflowLoadingNormalizedResultSchema: z.ZodObject<{
    packageName: z.ZodLiteral<'clover-ipg'>;
    workflowIds: z.ZodArray<typeof CloverRpaWorkflowIdSchema>;
    loadedWorkflowCount: z.ZodNumber;
    enabledWorkflowCount: z.ZodNumber;
    capabilities: typeof CloverRpaCapabilityMetadataSchema;
}> = z
    .object({
        packageName: z.literal('clover-ipg'),
        workflowIds: z.array(CloverRpaWorkflowIdSchema),
        loadedWorkflowCount: z.number().int().nonnegative(),
        enabledWorkflowCount: z.number().int().nonnegative(),
        capabilities: CloverRpaCapabilityMetadataSchema,
    })
    .strict() satisfies z.ZodType<CloverRpaWorkflowLoadingNormalizedResult>;

export const CloverRpaWorkflowLoadingLoadedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverRpaWorkflowLoadingOutcome.LOADED>;
    workflows: z.ZodArray<typeof CloverRpaWorkflowDescriptorSchema>;
    normalizedResult: typeof CloverRpaWorkflowLoadingNormalizedResultSchema;
}> = z
    .object({
        outcome: z.literal(CloverRpaWorkflowLoadingOutcome.LOADED),
        workflows: z.array(CloverRpaWorkflowDescriptorSchema).min(1),
        normalizedResult: CloverRpaWorkflowLoadingNormalizedResultSchema,
    })
    .strict();

export const CloverRpaWorkflowLoadingMissingPackageResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverRpaWorkflowLoadingOutcome.MISSING_PACKAGE
    >;
    packageName: z.ZodLiteral<'clover-ipg'>;
    message: z.ZodString;
}> = z
    .object({
        outcome: z.literal(CloverRpaWorkflowLoadingOutcome.MISSING_PACKAGE),
        packageName: z.literal('clover-ipg'),
        message: z.string().min(1),
    })
    .strict();

export const CloverRpaWorkflowLoadingMalformedWorkflowsResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverRpaWorkflowLoadingOutcome.MALFORMED_WORKFLOWS
    >;
    packageName: z.ZodLiteral<'clover-ipg'>;
    message: z.ZodString;
    workflowIds: z.ZodOptional<z.ZodArray<z.ZodString>>;
}> = z
    .object({
        outcome: z.literal(
            CloverRpaWorkflowLoadingOutcome.MALFORMED_WORKFLOWS
        ),
        packageName: z.literal('clover-ipg'),
        message: z.string().min(1),
        workflowIds: z.array(z.string().min(1)).optional(),
    })
    .strict();

export const CloverRpaWorkflowLoadingResponseDetailsSchema: z.ZodType<CloverRpaWorkflowLoadingResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverRpaWorkflowLoadingLoadedResponseDetailsSchema,
        CloverRpaWorkflowLoadingMissingPackageResponseDetailsSchema,
        CloverRpaWorkflowLoadingMalformedWorkflowsResponseDetailsSchema,
    ]);
